import { pitchHeadStart } from "./pitches";
import { rivalActive } from "./market";
import { addMod } from "./mods";
import { deskCount, furnishRoom, plotFor, plots } from "./layout";
import { C, type ProblemId, type StatId } from "@bd/content";
import { priceIndex, WEEKS_PER_YEAR, yearOf } from "./calendar";
import { BALANCE } from "./balance";
import { company, projectWork, queueFix, resolveDecision } from "./company";
import { compile, trueProblems, validateDesign, fixMods } from "./design";
import { runPlaytest } from "./playtest";
import { applyCousin, cousinPrint, payCousinPrint, useCousinLicence, type CousinCommand } from "./cousins";
import { creditLimit, factoryAvailable, makeLoan, placeOrder, quotePrint, rivalPrice, campaignCost, buyoutBlocked } from "./production";
import { focusHint } from "./review";
import { evaluate, newProduct, expectedFirstYear } from "./sales";
import type { Card, Company, Design, GameState, Product, Project, Room } from "./state";
import { furnFx, hasRoom, hireCost, levelCap, roomCost, roomFx, trainingCost, traitFx, perkChoice, fairPay } from "./studio";
import { isUnlocked, researchOptions, unlock, researchWeeks } from "./unlocks";
import { canonical, clamp, digits, inScope, log, money, newId, rng } from "./util";
import { perkFx } from "./perks";
import { foundImprint, setImprintFocus } from "./imprints";
import { bookBooth } from "./booths";
import { banCard, headlineCards, LINE_WORK, lineDesign, lineOptions, lineRoot, rotateOldest, type LineKind } from "./lines";

export type Command =
  | { type: "startProject"; design: Design; kind?: Project["kind"]; parentId?: string; licence?: string; cards?: Partial<Card>[] }
  | { type: "banCard"; cardId: string }
  | { type: "rotateSet"; productId: string }
  | { type: "editDesign"; projectId: string; design: Design }
  | { type: "cancelProject"; projectId: string }
  | { type: "assign"; staffId: string; projectId: string | null }
  | { type: "playtest"; projectId: string; pool: string }
  | { type: "fix"; projectId: string; problem: ProblemId }
  | { type: "advance"; projectId: string } // leave playtesting, or skip the current phase
  | { type: "crunch"; projectId: string; on: boolean }
  | { type: "marketing"; projectId: string; kind: string }
  | { type: "print"; projectId?: string; productId?: string; factory: string; copies: number; price?: number; channels?: string[] }
  | { type: "setPrice"; productId: string; price: number }
  | { type: "setChannels"; productId: string; channels: string[] }
  | { type: "clearance"; productId: string }
  | { type: "retire"; productId: string }
  | { type: "hire"; candidateId: string }
  | { type: "fire"; staffId: string }
  | { type: "train"; staffId: string; stat: StatId }
  | { type: "raise"; staffId: string; salary: number }
  | { type: "buildRoom"; room: string; x: number; y: number; w: number; h: number; door?: { x: number; y: number } }
  | { type: "demolishRoom"; roomId: string }
  | { type: "setDoor"; roomId: string; x: number; y: number }
  | { type: "placeFurniture"; roomId: string; kind: string; x: number; y: number; rot?: number }
  | { type: "removeFurniture"; roomId: string; furnitureId: string }
  | { type: "moveStudio"; level: "office" | "studio" | "hq" }
  | { type: "buyAnnex"; annex: string }
  | { type: "cancelResearch"; id: string }
  | { type: "acquireRival"; rival: string }
  | { type: "imprintFocus"; rival: string; focus: string }
  | { type: "bookBooth"; con: string; game: string }
  | { type: "openStore"; store: string }
  | { type: "closeStore"; store: string }
  | { type: "staffPerk"; staffId: string; perk: string }
  | { type: "signPitch"; pitchId: string }
  | { type: "setRd"; share: number }
  | { type: "crowdfund"; projectId: string }
  | { type: "loan"; amount: number }
  | { type: "repay"; loanId: string }
  | { type: "research"; id: string }
  | { type: "decide"; decisionId: string; option: string }
  | { type: "adoptPet"; breed: string; name: string }
  | { type: "rename"; name: string }
  | { type: "autopilot"; on: boolean }
  | CousinCommand;

export type CommandResult = { ok: true; id?: string; info?: any } | { ok: false; error: string };

const fail = (error: string): CommandResult => ({ ok: false, error });

const DECISIONS = new Set(["startProject", "playtest", "fix", "advance", "marketing", "print", "setPrice", "hire", "train", "buildRoom", "moveStudio", "loan", "research", "decide", "clearance", "imprintFocus", "bookBooth"]);

type Tap = (companyId: string, cmd: Command, result: CommandResult) => void;
let tap: Tap | null = null;
/** Lets a bot client record the moves it makes, to send them to the online server (soak tests). Changes nothing. */
export function tapCommands(f: Tap | null): void {
  tap = f;
}

export function apply(state: GameState, playerOrCompany: string, raw: Command): CommandResult {
  const cmd = canonical(raw);
  let co;
  try {
    co = company(state, playerOrCompany);
  } catch {
    return fail("No such company.");
  }
  const c = co;
  const res = inScope(c.id, () => applyTo(state, c, cmd));
  if (res.ok && DECISIONS.has(cmd.type)) co.stats.decisions += 1;
  tap?.(c.id, cmd, res);
  return res;
}

function applyTo(state: GameState, co: ReturnType<typeof company>, cmd: Command): CommandResult {
  const idx = priceIndex(state.week);
  const unlocked = (id: string) => isUnlocked(co, id);
  const project = (id: string) => co.projects.find((p) => p.id === id);
  const product = (id: string) => co.products.find((p) => p.id === id);
  const staff = (id: string) => co.staff.find((s) => s.id === id);

  switch (cmd.type) {
    case "startProject": {
      const kind = cmd.kind ?? (cmd.licence ? "license" : "base");
      let design = cmd.design;
      const size = C.size[design.scope ?? "standard"];
      if (!size) return fail("Unknown game size.");
      const order = ["garage", "office", "studio", "hq"];
      if (order.indexOf(co.level) < order.indexOf(size.level)) return fail(`${size.name} games need a${size.level === "office" ? "n" : ""} ${C.level[size.level].name.toLowerCase()}.`);
      if (yearOf(state.week) < size.year) return fail(`${size.name} games arrive in ${size.year}.`);
      let parent: Product | undefined;
      if (kind !== "base" && kind !== "license") {
        parent = co.products.find((x) => x.id === cmd.parentId);
        if (!parent) return fail("Which game is this a follow-up to?");
        const opt = lineOptions(state, co, parent).find((o) => o.kind === kind);
        if (!opt?.ok) return fail(opt?.why ?? "That game can't have one of those.");
        if (kind === "spinoff") {
          if (design.theme !== parent.design.theme) return fail(`A spin-off keeps the theme: ${C.theme[parent.design.theme].name}.`);
          if (design.mechanic === parent.design.mechanic) return fail("A spin-off needs a different mechanic.");
        } else {
          const auto = lineDesign(state, co, parent, kind as LineKind);
          const name = (cmd.design?.name ?? "").trim();
          design = { ...auto, name: name && name !== parent.design.name ? name : auto.name };
        }
      }
      let L: (typeof C.licence)[string] | undefined;
      if (kind === "license") {
        L = C.licence[cmd.licence ?? ""];
        if (!L || !(co.licenceOffers ?? []).some((o) => o.id === L!.id)) return fail("That licence isn't on offer.");
        if (design.theme !== L.theme) return fail(`${L.name} needs a ${C.theme[L.theme].name.toLowerCase()} game.`);
        const fee = Math.round(L.fee * idx * ((co.licenceOffers ?? []).find((o) => o.id === L!.id)?.feeMult ?? 1));
        if (co.cash < fee) return fail(`The licence fee is ${money(fee)}.`);
      }
      const issues = validateDesign(design, unlocked);
      if (issues.length) return fail(issues[0].message);
      const active = co.projects.filter((p) => p.phase !== "ready").length;
      const maxProjects = BALANCE.maxProjects[co.level];
      if (active >= maxProjects) return fail(`Your ${C.level[co.level].name.toLowerCase()} can run ${maxProjects} project${maxProjects > 1 ? "s" : ""} at once.`);
      const p: Project = {
        id: newId(state, "j"), design: structuredCopy(design), phase: "prototype", work: undefined as any, playtests: [], known: [], fixes: [], fixing: [],
        startedWeek: state.week, hype: co.nextHype ?? 0, marketing: 0, preorders: 0, crunch: false, kind,
      };
      if (parent) p.parentId = parent.id;
      if (design.format === "ccg") p.cards = headlineCards(state, cmd.cards);
      if (L) {
        co.cash -= Math.round(L.fee * idx * ((co.licenceOffers ?? []).find((o) => o.id === L!.id)?.feeMult ?? 1));
        p.licence = L.id;
        p.deadlineWeek = state.week + L.deadlineWeeks;
        co.licenceOffers = co.licenceOffers.filter((o) => o.id !== L!.id);
      }
      p.work = projectWork(p);
      for (const ph of Object.values(p.work)) ph.need = Math.max(1, Math.round(ph.need * LINE_WORK[kind]));
      co.nextHype = 0;
      const cost = Math.round(60 * idx);
      co.cash -= cost;
      co.projects.push(p);
      // the first idle people join the new project
      for (const s of co.staff) if (!s.projectId || !co.projects.some((x) => x.id === s.projectId && x.phase !== "ready")) s.projectId = p.id;
      log(state, "project", `${co.name} starts work on ${p.design.name}.`, co.id);
      return { ok: true, id: p.id };
    }
    case "editDesign": {
      const p = project(cmd.projectId);
      if (!p) return fail("No such project.");
      if (p.phase === "ready") return fail("It's ready to print; start a new edition instead.");
      const issues = validateDesign(cmd.design, unlocked);
      if (issues.length) return fail(issues[0].message);
      const coreChanged = cmd.design.mechanic !== p.design.mechanic || cmd.design.theme !== p.design.theme || cmd.design.format !== p.design.format;
      p.design = structuredCopy(cmd.design);
      if (coreChanged) {
        p.work = projectWork(p);
        p.phase = "prototype";
        p.fixes = [];
        p.known = [];
        p.fixing = [];
      } else {
        // reworking the sliders or parts costs a little prototype time
        const fresh = projectWork(p);
        p.work.art.need = fresh.art.need;
        p.work.rulebook.need = fresh.rulebook.need;
        if (p.phase !== "prototype") p.work.prototype.need += 2;
      }
      return { ok: true };
    }
    case "cancelProject": {
      const p = project(cmd.projectId);
      if (!p) return fail("No such project.");
      // backers who already paid get their money back, and they remember
      if (p.crowd?.funded) {
        co.cash -= p.crowd.raised ?? 0;
        co.reputation = clamp(co.reputation - BALANCE.crowdCancelRep, 0, 100);
        log(state, "crowd", `${p.design.name} is cancelled: ${co.name} refunds ${digits(p.crowd.backers)} backers, and they won't forget it.`, co.id);
      }
      co.projects = co.projects.filter((x) => x.id !== p.id);
      for (const s of co.staff) if (s.projectId === p.id) s.projectId = undefined;
      return { ok: true };
    }
    case "assign": {
      const s = staff(cmd.staffId);
      if (!s) return fail("No such person.");
      if (cmd.projectId && !project(cmd.projectId)) return fail("No such project.");
      s.projectId = cmd.projectId ?? undefined;
      return { ok: true };
    }
    case "playtest": {
      const p = project(cmd.projectId);
      if (!p) return fail("No such project.");
      if (p.phase === "prototype" && p.work.prototype.done < p.work.prototype.need * 0.5) return fail("The prototype isn't playable yet.");
      const pool = C.pool[cmd.pool];
      if (!pool) return fail("Unknown tester pool.");
      if (pool.needsRoom ? !hasRoom(co, pool.needsRoom) : !unlocked(`p:${pool.id}`)) return fail(`${pool.name} need ${pool.needsRoom ? "a Playtest lab" : "research"}.`);
      const fee = Math.round(pool.cost * idx);
      if (co.cash < fee) return fail(`Not enough cash (${money(fee)}).`);
      co.cash -= fee;
      const ev = evaluate(state, co, p);
      const c = compile(p.design, fixMods(p.fixes), ev.q.writing);
      const sev = trueProblems(p.design, c);
      const detect = clamp(roomFx(co, "detect") + furnFx(co, "detect") + co.staff.reduce((s, x) => s + (x.role === "playtest_lead" ? 0.08 + x.stats.systems / 1000 : 0) + traitFx(x, "detect"), 0) + perkFx(co, "detect"), 0, 0.5);
      const res = runPlaytest({ design: p.design, compiled: c, problems: sev, trueReplay: ev.replay, pool, detectBonus: detect, week: state.week, hint: focusHint(p.design) }, rng(state, "playtest"));
      p.playtests.push(res);
      p.rounds = (p.rounds ?? 0) + 1;
      if (p.rounds >= 3 && !co.flags.includes("three_rounds")) co.flags.push("three_rounds");
      if (res.testers.length >= 3 && res.testers.every((t) => t.mood === "hooked") && !co.flags.includes("all_hooked")) co.flags.push("all_hooked");
      if (p.playtests.length > 6) p.playtests.shift();
      for (const s of res.problems) if (s.confidence === "found" && !p.known.includes(s.problem)) p.known.push(s.problem);
      // a round of testing takes a week of the team's time
      if (p.phase === "prototype") p.work.prototype.need += 2;
      else p.work.playtest.need += 1;
      log(state, "playtest", `${p.design.name} playtest (${pool.name}): replay ${Math.round(res.replayIntent * 100)}%, ${res.problems.length ? res.problems.map((x) => C.problem[x.problem].name).join(", ") : "no problems spotted"}.`, co.id);
      return { ok: true, info: res };
    }
    case "fix": {
      const p = project(cmd.projectId);
      if (!p) return fail("No such project.");
      if (!C.problem[cmd.problem]) return fail("Unknown problem.");
      if (p.phase === "ready") return fail("Too late: it's ready to print.");
      const weeks = queueFix(p, cmd.problem);
      if (!weeks) return fail("Already fixed or in progress.");
      return { ok: true, info: { weeks } };
    }
    case "advance": {
      const p = project(cmd.projectId);
      if (!p) return fail("No such project.");
      if (p.phase === "prototype") {
        // cutting the prototype short: what's done is what you get
        p.work.prototype.quality *= 0.6 + 0.4 * (p.work.prototype.done / p.work.prototype.need);
        if (p.work.prototype.done < p.work.prototype.need * 0.3) p.work.prototype.skipped = true;
        p.work.prototype.done = p.work.prototype.need;
        p.phase = "playtest";
      } else if (p.phase === "playtest") {
        p.phase = "art";
      } else if (p.phase === "art" || p.phase === "rulebook") {
        for (const k of ["art", "rulebook"] as const) {
          const w = p.work[k];
          if (w.done < w.need) {
            w.quality = w.done > 0 ? w.quality * (0.5 + 0.5 * (w.done / w.need)) : 0;
            if (w.done < w.need * 0.3) w.skipped = true;
            w.done = w.need;
          }
        }
        p.fixing = [];
        p.phase = "ready";
      } else return fail("It's already ready to print.");
      return { ok: true };
    }
    case "crunch": {
      const p = project(cmd.projectId);
      if (!p) return fail("No such project.");
      p.crunch = cmd.on;
      for (const s of co.staff) if (s.projectId === p.id) s.crunch = cmd.on;
      return { ok: true };
    }
    case "marketing": {
      const p: { design: Design; hype: number; marketing?: number } | undefined = project(cmd.projectId) ?? product(cmd.projectId);
      const kind = C.marketing[cmd.kind];
      if (!p || !kind) return fail("Nothing to market.");
      if (yearOf(state.week) < kind.year) return fail(`${kind.name} arrive in ${kind.year}.`);
      const cost = campaignCost(co, kind.cost, state.week);
      if (co.cash < cost) return fail(`Not enough cash (${money(cost)}).`);
      co.cash -= cost;
      co.deductible = (co.deductible ?? 0) + cost;
      let gain = kind.hype * (kind.best.includes(p.design.audience) ? 1.3 : 1);
      for (const s of co.staff) if (s.role === "marketer") gain *= 1.15 + s.stats.charisma / 400;
      gain *= 1 + co.staff.reduce((s, x) => s + traitFx(x, "hype"), 0) + roomFx(co, "hype") + furnFx(co, "hype") + perkFx(co, "hype");
      gain *= 1 - p.hype * 0.5; // diminishing returns
      p.hype = clamp(p.hype + gain);
      if (p.marketing !== undefined) p.marketing += cost;
      return { ok: true, info: { hype: p.hype } };
    }
    case "print": {
      // "cousin:<company id>": print slots booked at a cousin's own print shop
      const atCousin = cmd.factory.startsWith("cousin:") ? cmd.factory.slice(7) : null;
      const f = atCousin ? C.factory.own_shop : C.factory[cmd.factory];
      if (!f) return fail("Unknown factory.");
      if (!atCousin && !factoryAvailable(co, f)) return fail(`${f.name} isn't available to you yet.`);
      const slots = atCousin ? cousinPrint(co, atCousin, cmd.copies, state.week) : null;
      if (typeof slots === "string") return fail(slots);
      if (!(cmd.copies > 0)) return fail("Print at least one copy.");
      if (!atCousin && cmd.copies < f.minOrder) return fail(`${f.name} needs at least ${digits(f.minOrder)} copies.`);
      let prod;
      let proj: Project | undefined;
      if (cmd.projectId) {
        proj = project(cmd.projectId);
        if (!proj) return fail("No such project.");
        if (proj.phase !== "ready") return fail("Finish art and rules first.");
        if (cmd.price !== undefined) proj.design.price = cmd.price;
        prod = newProduct(state, co, proj);
        if (cmd.channels) prod.channels = cmd.channels.filter((c) => unlocked(`ch:${c}`));
      } else if (cmd.productId) {
        prod = product(cmd.productId);
        if (!prod) return fail("No such product.");
      } else return fail("Print what?");
      const q = quotePrint(state, co, prod.design, f.id, cmd.copies);
      if (slots) {
        q.factory = cmd.factory;
        q.unitCost = slots.unitCost;
        q.setup = 0;
        q.total = slots.total;
        q.quality = C.factory.own_shop.quality;
        q.why = [`Print slots at ${state.companies.find((c) => c.id === atCousin)?.name ?? "a cousin's shop"}: ${money(slots.unitCost)} a copy`];
      }
      // a co-publisher pays their share of the first run
      const partner = proj?.partner ? state.companies.find((c) => c.id === proj!.partner!.companyId) : undefined;
      const partnerPays = partner && proj?.partner && partner.cash >= q.total * proj.partner.share ? Math.round(q.total * proj.partner.share) : 0;
      if (co.cash + partnerPays < q.total) return fail(`Not enough cash: this run costs ${money(q.total)}.`);
      const order = placeOrder(state, co, prod, q);
      if (slots) payCousinPrint(state, co, atCousin!, cmd.copies, q.total);
      if (proj) useCousinLicence(state, co, prod);
      if (partner && proj?.partner) {
        if (partnerPays) {
          partner.cash -= partnerPays;
          co.cash += partnerPays;
          (prod.shares ??= []).push({ companyId: partner.id, share: proj.partner.share, why: "copublish" });
          log(state, "cousin", `${partner.name} paid ${money(partnerPays)} towards printing ${prod.design.name}.`, co.id);
          log(state, "cousin", `You paid ${money(partnerPays)} as co-publisher of ${prod.design.name}.`, partner.id);
        } else log(state, "cousin", `${partner.name} couldn't pay their share, so ${prod.design.name} is all yours.`, co.id);
      }
      prod.unitCost = prod.unitCost ? (prod.unitCost * Math.max(1, prod.stock) + q.unitCost * q.copies) / (Math.max(1, prod.stock) + q.copies) : q.unitCost;
      if (proj) {
        co.products.push(prod);
        co.projects = co.projects.filter((x) => x.id !== proj!.id);
        prod.pending = proj; // kept until the first copies arrive and it launches
      }
      if (prod.retired) prod.retired = false;
      log(state, "print", `${co.name} prints ${digits(cmd.copies)} copies of ${prod.design.name} at ${f.name} (${money(q.total)}), arriving in ${order.arriveWeek - state.week} weeks.`, co.id);
      return { ok: true, id: prod.id, info: q };
    }
    case "setPrice": {
      const p = product(cmd.productId);
      if (!p || !(cmd.price > 0)) return fail("Bad price.");
      p.design.price = cmd.price;
      p.price = cmd.price;
      return { ok: true };
    }
    case "setChannels": {
      const p = product(cmd.productId);
      if (!p) return fail("No such product.");
      const ch = cmd.channels.filter((c) => unlocked(`ch:${c}`));
      if (!ch.length) return fail("Pick at least one channel you have.");
      p.channels = ch;
      return { ok: true };
    }
    case "clearance": {
      const p = product(cmd.productId);
      if (!p || p.stock <= 0) return fail("Nothing to clear.");
      p.clearance = 8;
      p.bargain = true;
      return { ok: true };
    }
    case "retire": {
      const p = product(cmd.productId);
      if (!p) return fail("No such product.");
      p.retired = true;
      return { ok: true };
    }
    case "hire": {
      const c = co.candidates.find((x) => x.id === cmd.candidateId);
      if (!c) return fail("That candidate has moved on.");
      if (co.staff.length >= levelCap(co)) return fail(`Your ${C.level[co.level].name.toLowerCase()} fits ${levelCap(co)} people. Move somewhere bigger.`);
      if (co.staff.length >= deskCount(co)) return fail("Everyone needs a desk. Build a room, or add a desk with Furnish.");
      const fee = hireCost(c);
      if (co.cash < fee) return fail(`Hiring costs ${money(fee)} up front.`);
      co.cash -= fee;
      co.candidates = co.candidates.filter((x) => x.id !== c.id);
      const { ask: _ask, ...s } = c;
      s.salary = c.ask;
      s.hiredWeek = state.week;
      co.staff.push(s);
      log(state, "hire", `${co.name} hires ${s.name}, ${C.role[s.role].name.toLowerCase()} (${money(s.salary)}/wk).`, co.id);
      return { ok: true, id: s.id };
    }
    case "fire": {
      const s = staff(cmd.staffId);
      if (!s || s.founder) return fail("You can't fire yourself.");
      co.cash -= s.salary * 4;
      co.staff = co.staff.filter((x) => x.id !== s.id);
      for (const o of co.staff) o.morale = clamp(o.morale - 4, 0, 100);
      return { ok: true };
    }
    case "train": {
      const s = staff(cmd.staffId);
      if (!s) return fail("No such person.");
      if (s.training) return fail("Already training.");
      const cost = trainingCost(s, state.week);
      if (co.cash < cost) return fail(`Training costs ${money(cost)}.`);
      co.cash -= cost;
      s.training = { stat: cmd.stat, weeksLeft: 4 };
      return { ok: true, info: { cost } };
    }
    case "raise": {
      const s = staff(cmd.staffId);
      if (!s || s.founder || !(cmd.salary > 0)) return fail("Bad raise.");
      const up = cmd.salary > s.salary;
      // the lift depends on the raise against fair pay, and only the first raise in a year counts
      const fair = fairPay(s, priceIndex(state.week));
      const share = (cmd.salary - s.salary) / Math.max(1, fair);
      const fresh = (s.lastRaiseWeek ?? -999) <= state.week - 52;
      s.salary = Math.round(cmd.salary);
      if (up) {
        if (fresh) s.morale = clamp(s.morale + Math.min(12, share * 60), 0, 100);
        s.lastRaiseWeek = state.week;
      } else s.morale = clamp(s.morale - 10, 0, 100);
      return { ok: true, info: { counted: up && fresh } };
    }
    case "buildRoom": {
      const t = C.room[cmd.room];
      if (!t) return fail("Unknown room.");
      if (cmd.w < t.minW || cmd.h < t.minH) return fail(`${t.name} must be at least ${t.minW} × ${t.minH} tiles.`);
      const plot = plotFor(co, cmd);
      if (!plot) return fail("Build inside one of your buildings.");
      if (!plot.rooms.includes(t.id)) return fail(plot.main ? `${t.name} needs a ${C.level[t.level].name.toLowerCase()}.` : `${t.name} doesn't go in the ${plot.name.toLowerCase()}.`);
      if (co.rooms.some((r) => overlaps(r, cmd))) return fail("It overlaps another room.");
      const cost = roomCost(t, cmd.w, cmd.h, state.week);
      if (co.cash < cost) return fail(`Not enough cash (${money(cost)}).`);
      co.cash -= cost;
      const room: Room = { id: newId(state, "r"), type: t.id, x: cmd.x, y: cmd.y, w: cmd.w, h: cmd.h, door: cmd.door ?? { x: cmd.x + Math.floor(cmd.w / 2), y: cmd.y + cmd.h }, furniture: [], builtWeek: state.week };
      co.rooms.push(room);
      // rooms come furnished: desks in rows, a centrepiece, decor
      furnishRoom(state, room);
      if (t.id === "print_shop") unlock(co, "fa:own_shop");
      if (t.id === "playtest_lab") unlock(co, "p:blind");
      log(state, "build", `${co.name} builds ${/^[aeiou]/i.test(t.name) ? "an" : "a"} ${t.name.toLowerCase()} (${money(cost)}).`, co.id);
      return { ok: true, id: room.id, info: { cost } };
    }
    case "setDoor": {
      const room = co.rooms.find((r) => r.id === cmd.roomId);
      if (!room) return fail("No such room.");
      if (!validDoor(room, cmd.x, cmd.y)) return fail("Doors go on a room's edge.");
      room.door = { x: cmd.x, y: cmd.y };
      return { ok: true };
    }
    case "demolishRoom": {
      const room = co.rooms.find((r) => r.id === cmd.roomId);
      if (!room) return fail("No such room.");
      co.rooms = co.rooms.filter((r) => r.id !== room.id);
      return { ok: true };
    }
    case "placeFurniture": {
      const room = co.rooms.find((r) => r.id === cmd.roomId);
      const k = C.furniture[cmd.kind];
      if (!room || !k) return fail("Can't place that.");
      if (k.year && yearOf(state.week) < k.year) return fail(`${k.name} arrives in ${k.year}.`);
      if (k.sweeps && (co.trophies ?? []).length < k.sweeps) return fail(`The ${k.name.toLowerCase()} is a reward: meet every goal in a year ${k.sweeps === 1 ? "once" : `${k.sweeps} times`} to earn it.`);
      if (k.sweeps && co.rooms.some((r) => r.furniture.some((x) => x.kind === k.id))) return fail(`There's only one ${k.name.toLowerCase()}, and it's already in your studio.`);
      const w = (cmd.rot ?? 0) % 2 ? k.h : k.w;
      const h = (cmd.rot ?? 0) % 2 ? k.w : k.h;
      if (cmd.x < 0 || cmd.y < 0 || cmd.x + w > room.w || cmd.y + h > room.h) return fail("It doesn't fit there.");
      if (!k.floor && room.furniture.some((f) => {
        const o = C.furniture[f.kind];
        if (!o || o.floor) return false;
        const ow = f.rot % 2 ? o.h : o.w;
        const oh = f.rot % 2 ? o.w : o.h;
        return cmd.x < f.x + ow && f.x < cmd.x + w && cmd.y < f.y + oh && f.y < cmd.y + h;
      })) return fail("Something is already there.");
      const cost = Math.round(k.cost * idx);
      if (co.cash < cost) return fail(`Not enough cash (${money(cost)}).`);
      co.cash -= cost;
      const f = { id: newId(state, "f"), kind: k.id, x: cmd.x, y: cmd.y, rot: cmd.rot ?? 0 };
      room.furniture.push(f);
      return { ok: true, id: f.id };
    }
    case "removeFurniture": {
      const room = co.rooms.find((r) => r.id === cmd.roomId);
      if (!room) return fail("No such room.");
      room.furniture = room.furniture.filter((f) => f.id !== cmd.furnitureId);
      return { ok: true };
    }
    case "moveStudio": {
      const order = ["garage", "office", "studio", "hq"];
      if (order.indexOf(cmd.level) !== order.indexOf(co.level) + 1) return fail("Move one step at a time.");
      const lvl = C.level[cmd.level];
      const cost = Math.round(lvl.moveCost * idx);
      if (co.cash < cost) return fail(`Moving costs ${money(cost)}.`);
      co.cash -= cost;
      co.level = cmd.level;
      if (cmd.level === "office" && co.stats.officeWeek === null) co.stats.officeWeek = state.week;
      if (cmd.level === "studio" && co.stats.studioWeek === null) co.stats.studioWeek = state.week;
      if (cmd.level === "hq" && co.stats.hqWeek === null) co.stats.hqWeek = state.week;
      log(state, "move", `${co.name} moves into a${cmd.level === "office" ? "n" : ""} ${lvl.name.toLowerCase()} (${money(cost)}).`, co.id);
      // the new place comes with one room set up, so it reads as an upgrade, not an empty floor
      const gift = MOVE_IN_ROOM[cmd.level];
      if (gift && !co.rooms.some((r) => r.type === gift)) giftRoom(state, co, gift, plots(co).find((p) => p.main)!);
      return { ok: true };
    }
    case "buyAnnex": {
      const t = C.annex[cmd.annex];
      if (!t) return fail("Unknown building.");
      const order = ["garage", "office", "studio", "hq"];
      if (order.indexOf(co.level) < order.indexOf(t.level)) return fail(`The ${t.name.toLowerCase()} needs a ${C.level[t.level].name.toLowerCase()} first.`);
      if ((co.annexes ?? []).some((a) => a.kind === t.id)) return fail(`You already have the ${t.name.toLowerCase()}.`);
      if (t.year && yearOf(state.week) < t.year) return fail(`The ${t.name.toLowerCase()} goes up for sale in ${t.year}.`);
      const cost = Math.round(t.cost * idx);
      if (co.cash < cost) return fail(`The ${t.name.toLowerCase()} costs ${money(cost)}.`);
      co.cash -= cost;
      co.annexes = [...(co.annexes ?? []), { id: newId(state, "b"), kind: t.id }];
      // its first room comes built and furnished
      const plot = plots(co).find((p) => p.kind === t.id);
      if (plot && t.rooms[0]) giftRoom(state, co, t.rooms[0], plot);
      log(state, "move", `${co.name} buys ${/^[aeiou]/i.test(t.name) ? "an" : "a"} ${t.name.toLowerCase()} next door (${money(cost)}).`, co.id);
      return { ok: true };
    }
    case "loan": {
      const limit = creditLimit(state, co);
      if (!(cmd.amount > 0) || cmd.amount > limit) return fail(`The bank will lend you up to ${money(limit)}.`);
      const loan = makeLoan(state, "bank", Math.round(cmd.amount), 2 * WEEKS_PER_YEAR, 0.09);
      co.loans.push(loan);
      co.cash += loan.principal;
      return { ok: true, id: loan.id, info: loan };
    }
    case "repay": {
      const l = co.loans.find((x) => x.id === cmd.loanId);
      if (!l) return fail("No such loan.");
      if (co.cash < l.principal) return fail("Not enough cash to repay it all.");
      co.cash -= l.principal;
      co.loans = co.loans.filter((x) => x.id !== l.id);
      return { ok: true };
    }
    case "research": {
      const opt = researchOptions(co, state.week).find((o) => o.id === cmd.id);
      if (!opt) return fail("You can't research that yet.");
      const slots = BALANCE.researchSlots[co.level] ?? 1;
      if ((co.researching ?? []).length >= slots) return fail(slots === 1 ? "Your team is already researching something. Wait for it, or grow into a studio for a second slot." : `All ${slots} research slots are busy.`);
      if (co.research < opt.cost) return fail(`Needs ${opt.cost} research points (you have ${Math.floor(co.research)}).`);
      co.research -= opt.cost;
      const weeks = researchWeeks(opt);
      co.researching = [...(co.researching ?? []), { id: opt.id, weeks, left: weeks }];
      log(state, "research_start", `${co.name} starts researching ${opt.name} (${weeks} weeks).`, co.id);
      return { ok: true, info: { weeks } };
    }
    case "acquireRival": {
      const rv = state.rivals.find((r) => r.id === cmd.rival);
      if (!rv || !rivalActive(rv, C.rival[rv.id], state.week)) return fail("They're not for sale.");
      if (co.level !== "studio" && co.level !== "hq") return fail("You need to be a studio before anyone takes your offer seriously.");
      const why = buyoutBlocked(state, co);
      if (why) return fail(why);
      const price = rivalPrice(state, rv.id);
      if (co.cash < price) return fail(`Buying ${C.rival[rv.id].name} costs ${money(price)}.`);
      co.cash -= price;
      rv.bust = true;
      rv.bustWeek = state.week;
      rv.acquiredBy = co.id;
      rv.acquiredWeek = state.week;
      rv.catalogueFor = undefined;
      // their back catalogue pays you for years; their fans come with them
      // the catalogue earns back about half the price over six years; the rest buys fans, reputation and one rival fewer
      addMod(state, co, { kind: "royalty", value: price / 620 / idx, target: "all", weeks: 6 * WEEKS_PER_YEAR, label: `${C.rival[rv.id].name} catalogue` });
      co.fans += 1500 + rv.released * 60;
      co.reputation = clamp(co.reputation + 5, 0, 100);
      // they keep publishing under their own name, as your imprint
      foundImprint(state, co, rv.id, price);
      log(state, "acquire", `${co.name} buys out ${C.rival[rv.id].name} for ${money(price)}. Their games and fans are yours now.`, co.id);
      return { ok: true };
    }
    case "imprintFocus": {
      const err = setImprintFocus(state, co, cmd.rival, cmd.focus ?? "");
      return err ? fail(err) : { ok: true };
    }
    case "bookBooth": {
      const err = bookBooth(state, co, cmd.con, cmd.game);
      return err ? fail(err) : { ok: true };
    }
    case "openStore": {
      const st = C.store[cmd.store];
      if (!st) return fail("Unknown town.");
      if ((co.stores ?? []).includes(st.id)) return fail(`You already have a shop in ${st.name}.`);
      if (co.level !== "studio" && co.level !== "hq") return fail("Shops of your own come once you're a studio.");
      if (yearOf(state.week) < st.year) return fail(`${st.name} has no space for you until ${st.year}.`);
      const cost = Math.round(st.cost * idx);
      if (co.cash < cost) return fail(`A shop in ${st.name} costs ${money(cost)}.`);
      co.cash -= cost;
      co.stores = [...(co.stores ?? []), st.id];
      co.fans += st.fans;
      log(state, "store", `${co.name} opens a shop in ${st.name} (${money(cost)}).`, co.id);
      return { ok: true };
    }
    case "crowdfund": {
      const p = co.projects.find((x) => x.id === cmd.projectId);
      if (!p) return fail("No such project.");
      if (!isUnlocked(co, "ch:backerbox")) return fail("Crowdfunding needs the BackerBox channel (research it under Business).");
      if (p.crowd) return fail("This game has had its campaign.");
      if (p.phase === "prototype") return fail("Backers want to see a working prototype first.");
      if (p.kind !== "base" && p.kind !== "spinoff" && p.kind !== "edition") return fail("Backers fund new boxes: a base game, a spin-off or an edition.");
      if (co.projects.some((x) => x.crowd && !x.crowd.done)) return fail("One campaign at a time: backers can only follow one.");
      // the goal: enough backers to pay for a first print run of a fifth of what it should sell
      p.expectFY ??= expectedFirstYear(state, co, p);
      const goal = Math.max(300, Math.round(p.expectFY * 0.1));
      p.crowd = { endsWeek: state.week + 4, backers: 0, deadline: state.week + BALANCE.crowdDeadline, done: false, goal };
      log(state, "crowd", `${p.design.name}'s crowdfunding campaign is live for four weeks: it needs ${digits(goal)} backers.`, co.id);
      return { ok: true };
    }
    case "setRd": {
      if (!(cmd.share >= 0)) return fail("Pick a share of sales.");
      co.rdShare = Math.min(BALANCE.rdMax, Math.round(cmd.share * 100) / 100);
      return { ok: true };
    }
    case "signPitch": {
      const pt = (co.pitches ?? []).find((x) => x.id === cmd.pitchId);
      if (!pt) return fail("That pitch has gone to someone else.");
      if (co.cash < pt.advance) return fail(`${pt.designer} wants ${money(pt.advance)} up front.`);
      const active = co.projects.filter((p) => p.phase !== "ready").length;
      const maxProjects = BALANCE.maxProjects[co.level];
      if (active >= maxProjects) return fail(`Your ${C.level[co.level].name.toLowerCase()} can run ${maxProjects} project${maxProjects > 1 ? "s" : ""} at once.`);
      const p: Project = {
        id: newId(state, "j"), design: structuredCopy(pt.design), phase: "prototype", work: undefined as any, playtests: [], known: [], fixes: [], fixing: [],
        startedWeek: state.week, hype: co.nextHype ?? 0, marketing: 0, preorders: 0, crunch: false, kind: "base",
        pitch: { designer: pt.designer, stars: pt.stars, royalty: pt.royalty },
      };
      p.work = projectWork(p);
      // the designer did the first part: most of the prototype, a little art, all of it good
      const head = pitchHeadStart(pt.stars);
      p.work.prototype.done = Math.round(p.work.prototype.need * head.prototype);
      p.work.prototype.quality = head.quality;
      p.work.art.done = Math.round(p.work.art.need * head.art);
      p.work.art.quality = head.quality * 0.9;
      co.nextHype = 0;
      co.cash -= pt.advance;
      co.deductible = (co.deductible ?? 0) + pt.advance;
      co.pitches = (co.pitches ?? []).filter((x) => x.id !== pt.id);
      co.projects.push(p);
      for (const s of co.staff) if (!s.projectId || !co.projects.some((x) => x.id === s.projectId && x.phase !== "ready")) s.projectId = p.id;
      log(state, "project", `${co.name} signs ${pt.designer}'s ${p.design.name} for ${money(pt.advance)} and ${Math.round(pt.royalty * 100)}% of sales.`, co.id);
      return { ok: true, id: p.id };
    }
    case "staffPerk": {
      const s = co.staff.find((x) => x.id === cmd.staffId);
      if (!s) return fail("They've left.");
      const tier = perkChoice(s);
      const opt = tier?.options.find((o) => o.id === cmd.perk);
      if (!tier || !opt) return fail("That strength isn't on offer for them.");
      s.perks = [...(s.perks ?? []), opt.id];
      log(state, "perk", `${s.name} becomes known for being a ${opt.name.toLowerCase()}: ${opt.desc}`, co.id);
      return { ok: true };
    }
    case "closeStore": {
      const st = C.store[cmd.store];
      if (!st || !(co.stores ?? []).includes(st.id)) return fail("You don't have a shop there.");
      const back = Math.round(st.cost * idx * 0.3);
      co.stores = (co.stores ?? []).filter((x) => x !== st.id);
      co.cash += back;
      log(state, "store", `${co.name} closes its shop in ${st.name} and gets ${money(back)} back.`, co.id);
      return { ok: true };
    }
    case "cancelResearch": {
      const r = (co.researching ?? []).find((x) => x.id === cmd.id);
      if (!r) return fail("That isn't being researched.");
      // the same study can run twice: stop just one
      co.researching = (co.researching ?? []).filter((x) => x !== r);
      return { ok: true };
    }
    case "banCard": {
      const err = banCard(state, co, cmd.cardId);
      return err ? fail(err) : { ok: true };
    }
    case "rotateSet": {
      const prod = co.products.find((x) => x.id === cmd.productId);
      if (!prod) return fail("No such game.");
      const err = rotateOldest(state, co, lineRoot(co, prod));
      return err ? fail(err) : { ok: true };
    }
    case "decide": {
      return resolveDecision(state, co, cmd.decisionId, cmd.option) ? { ok: true } : fail("That decision is gone.");
    }
    case "adoptPet": {
      const b = C.breed[cmd.breed];
      if (!b) return fail("Unknown breed.");
      if (co.pets.length >= 1 + ["garage", "office", "studio", "hq"].indexOf(co.level) * 2 + perkFx(co, "petSlots")) return fail("No room for another pet here.");
      const r = rng(state, `pets:${co.id}`);
      const pet = { id: newId(state, "pet"), name: cmd.name.slice(0, 20) || "Biscuit", breed: b.id, spot: r.pick(["on the warehouse boxes", "under the design desk", "in the sunny window", "on the sofa", "in an empty game box"]), quirk: r.pick(["steals dice", "sleeps in game boxes", "sits on rulebooks", "greets every visitor"]) };
      co.pets.push(pet);
      co.cash -= Math.round(80 * idx);
      return { ok: true, id: pet.id };
    }
    case "rename": {
      const n = cmd.name.trim().slice(0, 40);
      if (!n) return fail("Name can't be empty.");
      co.name = n;
      return { ok: true };
    }
    case "autopilot": {
      co.autopilot = cmd.on;
      return { ok: true };
    }
    case "cousinPoach":
    case "coPublish":
    case "licenceCousin":
    case "sellPrintSlots":
    case "crowdVote":
      return applyCousin(state, co, cmd);
  }
}

/** A door sits on a room edge: the front and right edges use the tile just outside, the back and left the tile just inside. */
export function validDoor(r: { x: number; y: number; w: number; h: number }, x: number, y: number): boolean {
  const alongX = x >= r.x && x < r.x + r.w;
  const alongY = y >= r.y && y < r.y + r.h;
  return (alongX && y === r.y + r.h) || (alongY && x === r.x + r.w) || (alongX && y === r.y && r.y > 0) || (alongY && x === r.x && r.x > 0);
}

/** The door spot nearest to a tile the player clicked. */
export function nearestDoor(r: { x: number; y: number; w: number; h: number }, tx: number, ty: number): { x: number; y: number } {
  const cx = Math.min(r.x + r.w - 1, Math.max(r.x, tx));
  const cy = Math.min(r.y + r.h - 1, Math.max(r.y, ty));
  const options = [
    { x: cx, y: r.y + r.h, d: Math.abs(ty - (r.y + r.h)) },
    { x: r.x + r.w, y: cy, d: Math.abs(tx - (r.x + r.w)) },
    ...(r.y > 0 ? [{ x: cx, y: r.y, d: Math.abs(ty - r.y) + 0.5 }] : []),
    ...(r.x > 0 ? [{ x: r.x, y: cy, d: Math.abs(tx - r.x) + 0.5 }] : []),
  ];
  options.sort((a, b) => a.d - b.d);
  return { x: options[0].x, y: options[0].y };
}

function overlaps(a: { x: number; y: number; w: number; h: number }, b: { x: number; y: number; w: number; h: number }): boolean {
  return a.x < b.x + b.w && b.x < a.x + a.w && a.y < b.y + b.h && b.y < a.y + a.h;
}

function structuredCopy<T>(v: T): T {
  return JSON.parse(JSON.stringify(v));
}

/** The room a move comes with (if you don't have one yet). */
const MOVE_IN_ROOM: Record<string, string> = { office: "playtest_lab", studio: "art_studio", hq: "break_room" };

/** Build a furnished room for free in the first free spot of a building (moving in, a new annex). */
function giftRoom(state: GameState, co: Company, type: string, plot: { x: number; y: number; w: number; h: number }): void {
  const t = C.room[type];
  if (!t) return;
  const w = Math.min(plot.w - 1, Math.max(t.minW, 6));
  const h = Math.min(plot.h - 1, Math.max(t.minH, 4));
  // the far corner, out of the way of where you'd start building
  for (let y = plot.y + plot.h - 1 - h; y >= plot.y; y--) for (let x = plot.x + plot.w - w; x >= plot.x; x--) {
    const r = { x, y, w, h };
    if (co.rooms.some((o) => overlaps(o, r))) continue;
    const room: Room = { id: newId(state, "r"), type: t.id, x, y, w, h, door: { x: x + Math.floor(w / 2), y: y + h }, furniture: [], builtWeek: state.week };
    co.rooms.push(room);
    furnishRoom(state, room);
    if (t.id === "print_shop") unlock(co, "fa:own_shop");
    if (t.id === "playtest_lab") unlock(co, "p:blind");
    log(state, "build", `It comes with ${/^[aeiou]/i.test(t.name) ? "an" : "a"} ${t.name.toLowerCase()}, ready to use.`, co.id);
    return;
  }
}
